import React, { useState, forwardRef, useImperativeHandle } from "react";
import PrimaryButton from "../Buttons/PrimaryButton";
import SecondaryButton from "../Buttons/SecondaryButton";
import { MultiStepProgressBar } from "./MultiStepProgressBar";
import {
  faArrowRight,
  faArrowLeft,
  faAnglesRight,
  faAnglesLeft,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import { useMultiStepNavigation } from "./hooks";
import { UCLColorKey } from "../colors/uclColors";
import { SidebarComponent } from "@syncfusion/ej2-react-navigations";
import classNames from "classnames";
import { RelativityTooltipV2 } from "../Tooltip/RelativityTooltipV2";

export interface MultiStep<T extends MultiStepItemProps = MultiStepItemProps> extends Step<T> {
  substeps?: Step<T>[];
}

export interface Step<T extends MultiStepItemProps> {
  id: string;
  label: string;
  available?: boolean; // Controls step visibility while preserving indices
  component?: React.FC<Partial<T>>;
  props?: Partial<T>;
  sideBar?: {
    title: React.ReactNode;
    content: React.ReactNode;
  };
  nextButtonLabel?: string;
  nextButtonSettings?: {
    isDisabled?: boolean;
    isDisabledTooltipContent?: string;
    showLoading?: boolean;
  };
}

export interface MultiStepContainerProps<T extends MultiStepItemProps = MultiStepItemProps> {
  steps: MultiStep<T>[];
  defaultStep?: string;
  color: UCLColorKey;
  backButtonLabel?: string;
  nextButtonLabel?: string;
  width?: string | number;
  height?: string | number;
  className?: string;
  beforeStepChange?: (from: string, to: string) => boolean | Promise<boolean>;
  onStepChange?: (stepId: string) => void;
  nextButtonSettings?: {
    isLoading?: boolean;
    isDirty?: boolean;
  };
  quickNavigate?: {
    targetStepId: string;
    label?: string;
    shouldShow?: boolean;
  };
  ref?: React.Ref<MultiStepContainerModule>;
}

export interface MultiStepItemProps {
  registerSubmit?: (submitFn: () => Promise<boolean>) => void;
  progressBarReplacementContent?: React.ReactNode;
  handleNext?: (skipValidation?: boolean) => void;
}

export interface MultiStepContainerModule {
  handleNext: () => void;
}

  export const MultiStepContainer = forwardRef(function MultiStepContainer<
    T extends MultiStepItemProps = MultiStepItemProps
  >(
    props: MultiStepContainerProps<T>,
    ref: React.Ref<MultiStepContainerModule>
  ) {
    const {
      steps,
      defaultStep,
      color,
      backButtonLabel = "Go Back",
      nextButtonLabel = "Continue",
      width = "100%",
      height = "100%",
      className,
      beforeStepChange,
      onStepChange,
      nextButtonSettings,
      quickNavigate,
    } = props;

    const navigation = useMultiStepNavigation({
      steps,
      defaultStep,
      beforeStepChange,
      onStepChange,
      isDirty: nextButtonSettings?.isDirty,
    });

    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      handleNext: () => {
        navigation.handleNext();
      },
    }));

    // Handle quick navigate
    const handleQuickNavigate = () => {
      if (quickNavigate?.targetStepId) {
        navigation.handleStepClick(quickNavigate.targetStepId);
      }
    };

    // Check if quick navigate should be shown
    const shouldShowQuickNavigate = quickNavigate?.shouldShow ?? true;
    const isQuickNavigateTargetCurrent =
      navigation.currentStep.id === quickNavigate?.targetStepId;

    // Generate class names
    const containerClasses = [
      "ucl-multistep-container",
      width !== "100%" ? "ucl-multistep-container--has-custom-width" : "",
      height !== "100%" ? "ucl-multistep-container--has-custom-height" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const cardClasses = ["ucl-multistep-container__card"]
      .filter(Boolean)
      .join(" ");

    // Combine styles with CSS custom properties
    const containerStyle: React.CSSProperties = {
      "--ucl-multistep-width": typeof width === "number" ? `${width}px` : width,
      "--ucl-multistep-height":
        typeof height === "number" ? `${height}px` : height,
    } as React.CSSProperties;

    const progressBarCurrentStepId = navigation.currentSubstepId
      ? `${navigation.currentStep.id}:${navigation.currentSubstepId}`
      : navigation.currentStep.id;

    // Get the current component to display
    const getCurrentComponent = () => {
      if (navigation.currentSubstepId && navigation.currentStep.substeps) {
        const currentSubstep = navigation.currentStep.substeps.find(
          (substep) => substep.id === navigation.currentSubstepId
        );
        return currentSubstep?.component
          ? currentSubstep.component
          : navigation.currentStep.component
            ? navigation.currentStep.component
            : null;
      }
      return navigation.currentStep.component
        ? navigation.currentStep.component
        : null;
    };

    const getCurrentProps = () => {
      if (navigation.currentSubstepId && navigation.currentStep.substeps) {
        const currentSubstep = navigation.currentStep.substeps.find(
          (substep) => substep.id === navigation.currentSubstepId
        );
        return currentSubstep?.props
          ? currentSubstep.props
          : navigation.currentStep.props
            ? navigation.currentStep.props
            : null;
      }
      return navigation.currentStep.props ? navigation.currentStep.props : null;
    };

    const getCurrentSidebar = () => {
      if (navigation.currentSubstepId && navigation.currentStep.substeps) {
        const currentSubstep = navigation.currentStep.substeps.find(
          (substep) => substep.id === navigation.currentSubstepId
        );
        return currentSubstep?.sideBar
          ? currentSubstep.sideBar
          : navigation.currentStep.sideBar
            ? navigation.currentStep.sideBar
            : null;
      }
      return navigation.currentStep.sideBar
        ? navigation.currentStep.sideBar
        : null;
    };

    const getCurrentNextButtonLabel = () => {
      // If current step has substeps and we're on a substep
      if (navigation.currentSubstepId && navigation.currentStep.substeps) {
        const currentSubstep = navigation.currentStep.substeps.find(
          (substep) => substep.id === navigation.currentSubstepId
        );
        return currentSubstep?.nextButtonLabel
          ? {
              currentNextButtonLabel: currentSubstep.nextButtonLabel,
              currentNextButtonSettings: currentSubstep.nextButtonSettings,
            }
          : { currentNextButtonLabel: nextButtonLabel, currentNextButtonSettings: { showLoading: currentSubstep?.nextButtonSettings?.showLoading } };
      }
      return navigation.currentStep.nextButtonLabel
        ? {
            currentNextButtonLabel: navigation.currentStep.nextButtonLabel,
            currentNextButtonSettings:
              navigation.currentStep.nextButtonSettings,
          }
        : { currentNextButtonLabel: nextButtonLabel, currentNextButtonSettings: { showLoading: navigation.currentStep.nextButtonSettings?.showLoading } };
    };

    const ItemComponent = getCurrentComponent() as React.FC<
      Partial<MultiStepItemProps>
    >;

    const currentProps = getCurrentProps();
    const currentSidebar = getCurrentSidebar();
    const { currentNextButtonLabel, currentNextButtonSettings } =
      getCurrentNextButtonLabel();

    return (
      <div className={containerClasses} style={containerStyle}>
        <div className="ucl-multistep-container__progress-wrapper">
          {currentProps?.progressBarReplacementContent ? (
            currentProps.progressBarReplacementContent
          ) : (
            <MultiStepProgressBar
              steps={steps}
              currentStepId={progressBarCurrentStepId}
              color={color}
              onStepClick={navigation.handleStepClick}
            />
          )}
        </div>

        <div className="ucl-multistep-container__content-wrapper">
          <div className={cardClasses}>
            <div className="ucl-multistep-container__content">
              {ItemComponent && (
                <ItemComponent
                  registerSubmit={navigation.registerSubmit}
                  handleNext={navigation.handleNext}
                  {...currentProps}
                />
              )}
            </div>

            <div className="ucl-multistep-container__footer">
              <SecondaryButton
                onClick={navigation.handleBack}
                disabled={navigation.isFirstStep}
                // color={color}
                iconPosition="left"
                iconProps={{ icon: faArrowLeft }}
              >
                {backButtonLabel}
              </SecondaryButton>

              <div className="ucl-multistep-container__footer-right">
                {shouldShowQuickNavigate &&
                  quickNavigate?.targetStepId &&
                  !isQuickNavigateTargetCurrent && (
                    <SecondaryButton
                      onClick={handleQuickNavigate}
                      // color={color}
                      iconPosition="right"
                      iconProps={{ icon: faBolt }}
                      minimal={true}
                    >
                      {quickNavigate.label || "Quick Navigate"}
                    </SecondaryButton>
                  )}

                <RelativityTooltipV2
                  shouldShowTooltip={
                    !!currentNextButtonSettings?.isDisabled &&
                    !!currentNextButtonSettings?.isDisabledTooltipContent
                  }
                  content={
                    currentNextButtonSettings?.isDisabledTooltipContent || ""
                  }
                >
                  <PrimaryButton
                    key={
                      currentNextButtonLabel +
                      nextButtonSettings?.isDirty +
                      nextButtonSettings?.isLoading
                    }
                    text={currentNextButtonLabel || nextButtonLabel}
                    onClick={() => navigation.handleNext()}
                    iconPosition="right"
                    iconProps={{ icon: faArrowRight }}
                    loadingProgressContent={currentNextButtonLabel || nextButtonLabel}
                    loadingSuccessContent={currentNextButtonLabel || nextButtonLabel}
                    isLoading={(currentNextButtonSettings?.showLoading || navigation.isDirtyAndNext) && navigation.nextLoading === true}
                    disabled={
                      currentNextButtonSettings?.isDisabled 
                    }
                  />
                </RelativityTooltipV2>
              </div>
            </div>
          </div>

          <div
            className={classNames(
              "ucl-multistep-container__sidebar",
              currentSidebar
                ? "ucl-multistep-container__sidebar--visible"
                : "ucl-multistep-container__sidebar--hidden"
            )}
            style={{ visibility: currentSidebar ? "visible" : "hidden" }}
          >
            <SidebarComponent
              id="ucl-multistep-sidebar"
              position="Right"
              target=".ucl-multistep-container__content-wrapper"
              type="Push"
              enableDock={true}
              animate={false}
              enableGestures={false}
              width="300px"
              dockSize={55}
              isOpen={currentSidebar ? isSidebarOpen : false}
              //Hiding through style to avoid error on removing node. Visibility to keep width of the sidebar. Improve later.
              style={{ visibility: currentSidebar ? "visible" : "hidden" }}
            >
              <section
                className={classNames(
                  "ucl-multistep-container__sidebar-container",
                  isSidebarOpen
                    ? "ucl-multistep-container__sidebar-container--open"
                    : "ucl-multistep-container__sidebar-container--closed"
                )}
                key={isSidebarOpen && currentSidebar ? "open" : "closed"}
              >
                <div className="ucl-multistep-container__sidebar-header">
                  <div className="ucl-multistep-container__sidebar-header-icon">
                    <SecondaryButton
                      minimal={true}
                      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                      iconPosition="left"
                      iconProps={{
                        icon: isSidebarOpen ? faAnglesRight : faAnglesLeft,
                        size: "lg",
                      }}
                    />
                  </div>
                  {isSidebarOpen && (
                    <div className="ucl-multistep-container__sidebar-header-title">
                      {currentSidebar?.title}
                    </div>
                  )}
                </div>
                {isSidebarOpen && (
                  <div className="ucl-multistep-container__sidebar-content">
                    {currentSidebar?.content}
                  </div>
                )}
              </section>
            </SidebarComponent>
          </div>
        </div>
      </div>
    );
  }
);
